import React, { useState } from 'react';
import { getImageUrl } from '../services/api';

function VideoResults({ result, isProcessing }) {
  const [selectedFrame, setSelectedFrame] = useState(0);

  if (isProcessing) {
    return (
      <div className="video-results card">
        <h3 className="card-title">Video Analysis</h3>
        <div className="shimmer-box" style={{ height: 200 }}></div>
      </div>
    );
  }

  if (!result) return null;

  const frames = result.frameResults || [];
  const timeline = result.complianceTimeline || [];
  const currentFrame = frames[selectedFrame];

  // Compliance timeline bar chart
  const maxBarHeight = 80;

  return (
    <div className="video-results">
      {/* Overview Card */}
      <div className="card video-overview">
        <div className="card-header">
          <h3 className="card-title">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
              <path d="M2 3a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V3zm5 3v6l5-3-5-3z"/>
            </svg>
            Video Analysis Results
          </h3>
          <span className="processing-time">{result.processingTimeMs}ms total</span>
        </div>

        <div className="video-stats-grid">
          <div className="video-stat">
            <span className="video-stat-value">{result.framesAnalyzed}</span>
            <span className="video-stat-label">Frames Analyzed</span>
          </div>
          <div className="video-stat">
            <span className={`video-stat-value ${result.overallCompliance >= 80 ? 'text-success' : result.overallCompliance >= 50 ? 'text-warning' : 'text-danger'}`}>
              {result.overallCompliance}%
            </span>
            <span className="video-stat-label">Overall Compliance</span>
          </div>
          <div className="video-stat">
            <span className="video-stat-value">{result.summary?.maxPersonsInFrame || 0}</span>
            <span className="video-stat-label">Max Persons/Frame</span>
          </div>
          <div className="video-stat">
            <span className="video-stat-value text-danger">{result.summary?.totalViolationEvents || 0}</span>
            <span className="video-stat-label">Total Violations</span>
          </div>
          <div className="video-stat">
            <span className="video-stat-value text-danger">{result.summary?.framesWithViolations || 0}</span>
            <span className="video-stat-label">Frames w/ Violations</span>
          </div>
          <div className="video-stat">
            <span className="video-stat-value">
              {result.videoInfo ? `${Math.round(result.videoInfo.duration)}s` : '-'}
            </span>
            <span className="video-stat-label">Video Duration</span>
          </div>
        </div>
      </div>

      {/* Compliance Timeline */}
      {timeline.length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 16 }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
              <path d="M2 15V3h2v12H2zm4-2V5h2v8H6zm4-4V7h2v4h-2zm4-2V9h2v2h-2z"/>
            </svg>
            Compliance Timeline
          </h3>
          <div className="timeline-chart">
            {timeline.map((point, i) => {
              const barHeight = Math.max(4, (point.score / 100) * maxBarHeight);
              let barColor = '#10b981';
              if (point.score < 50) barColor = '#ef4444';
              else if (point.score < 80) barColor = '#f59e0b';

              return (
                <div
                  key={i}
                  className={`timeline-bar-wrapper ${selectedFrame === i ? 'selected' : ''}`}
                  onClick={() => setSelectedFrame(i)}
                  title={`t=${point.time}s — ${point.score}% compliance, ${point.persons} persons`}
                >
                  <div className="timeline-bar" style={{ height: barHeight, background: barColor }}></div>
                  <span className="timeline-label">{point.time}s</span>
                </div>
              );
            })}
          </div>
          <div className="timeline-legend">
            <span>Click a bar to view that frame's analysis</span>
          </div>
        </div>
      )}

      {/* Selected Frame Detail */}
      {currentFrame && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              Frame at {currentFrame.timestamp}s
            </h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="frame-nav-btn"
                disabled={selectedFrame === 0}
                onClick={() => setSelectedFrame(s => s - 1)}
              >
                Prev
              </button>
              <span className="frame-counter">{selectedFrame + 1} / {frames.length}</span>
              <button
                className="frame-nav-btn"
                disabled={selectedFrame === frames.length - 1}
                onClick={() => setSelectedFrame(s => s + 1)}
              >
                Next
              </button>
            </div>
          </div>

          {currentFrame.annotatedImage && (
            <div className="annotated-image-container">
              <img
                src={getImageUrl(currentFrame.annotatedImage)}
                alt={`Frame at ${currentFrame.timestamp}s`}
                className="annotated-image"
              />
            </div>
          )}

          <div className="frame-detail-grid">
            <div className="frame-detail-item">
              <span className="frame-detail-label">Compliance</span>
              <span className={`frame-detail-value ${
                currentFrame.complianceScore >= 80 ? 'text-success' :
                currentFrame.complianceScore >= 50 ? 'text-warning' : 'text-danger'
              }`}>
                {currentFrame.complianceScore}%
              </span>
            </div>
            <div className="frame-detail-item">
              <span className="frame-detail-label">Persons</span>
              <span className="frame-detail-value">{currentFrame.summary?.totalPersons || 0}</span>
            </div>
            <div className="frame-detail-item">
              <span className="frame-detail-label">Helmets</span>
              <span className="frame-detail-value text-success">{currentFrame.summary?.helmetsDetected || 0}</span>
            </div>
            <div className="frame-detail-item">
              <span className="frame-detail-label">Violations</span>
              <span className="frame-detail-value text-danger">{currentFrame.summary?.violations || 0}</span>
            </div>
          </div>

          {currentFrame.detectedObjects?.length > 0 && (
            <div className="detection-table" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Object</th>
                    <th>Confidence</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentFrame.detectedObjects.map((obj, i) => (
                    <tr key={i}>
                      <td>{obj.label}</td>
                      <td>{Math.round(obj.confidence * 100)}%</td>
                      <td>
                        <span className={`status-chip status-${obj.complianceStatus}`}>
                          {obj.complianceStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Aggregated Alerts */}
      {result.alerts?.length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 12 }}>
            Aggregated Alerts ({result.alerts.length})
          </h3>
          <div className="alerts-list">
            {result.alerts.map((alert, i) => (
              <div key={i} className={`alert-item alert-${alert.severity}`}>
                <div className="alert-content">
                  <span className="alert-badge">{alert.severity.toUpperCase()}</span>
                  <p className="alert-message">{alert.message}</p>
                  {alert.rule && <span className="alert-rule">Rule: {alert.rule}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoResults;
